import React, { useState, useContext } from 'react';
import { FETCH_STATE } from '../../utils/app_constants';
import { trackCustomEvents } from '../../utils/appInsight_analytics';
import StatusContext from '../../context/StatusContext';
import SyncUpProductContext from 'context/SyncUpProductContext';
import deviceService from '../../services/deviceService';
import useManualFetcher from '../../hooks/useManualFetcher';

import IMEIFileHandler from './IMEIFileHandler';
import IMEIListDisplay from './IMEIListDisplay';
import IMEIConfirmationModal from './IMEIConfirmationModal';

import style from './UploadDriveIMEIcsv.module.css';

const UploadDriveIMEIcsv = () => {
  // Raw CSV file chosen by user (not parsed)
  const [selectedFile, setSelectedFile] = useState(null);

  // Controls the confirmation modal before upload
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Stores API result JSON returned from backend after upload
  const [uploadResult, setUploadResult] = useState(null);

  // Determines whether to render the upload summary section
  const [showResult, setShowResult] = useState(false);

  // Global toast handlers
  const { addSuccessMessage, addErrorMessage } = useContext(StatusContext);

  // Context used to decide app-specific endpoint calls
  const { syncUpAppName } = useContext(SyncUpProductContext);

  /**
   * useManualFetcher returns a tuple:
   * fetchState → IDLE/LOADING/COMPLETE/ERROR
   * triggerUpload → function to trigger the upload request
   * error → captured error if failure
   * resetFetchState → reset to initial state
   */
  const [fetchState, triggerUpload, error, resetFetchState] = useManualFetcher({
    // Called when triggerUpload() is executed
    action: () => {
      if (!selectedFile) {
        return Promise.reject(new Error('No file selected'));
      }

      // Custom telemetry event
      trackCustomEvents('Upload IMEI CSV', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size
      });

      // Prepare multipart/form-data body
      const form = new FormData();
      form.append('file', selectedFile);
      return deviceService.bulkUploadIMEIICCIDmappings({ form, syncUpAppName });
    },

    // Fires when action() resolves SUCCESSFULLY
    onLoad: data => {
      setUploadResult(data || {});
      addSuccessMessage({ message: 'CSV uploaded successfully.' });

      /**
       * IMPORTANT:
       * Remove file from UI only after SUCCESS.
       * So after user sees API summary, they do not still see old file.
       * In case of error, we DO NOT clear it.
       */
      setSelectedFile(null);
    },

    // Fires when action() throws or rejects
    onError: err => {
      addErrorMessage({ error: err || 'Unknown error during upload.' });
    },

    // Always runs after either success or error
    onComplete: () => {
      // Close modal regardless of outcome
      setIsModalOpen(false);

      // Trigger summary block to be shown beneath
      setShowResult(true);
    }
  });

  /** Opens modal only if user has selected a file */
  const openModal = () => {
    if (selectedFile) setIsModalOpen(true);
    else addErrorMessage({ details: 'Please select a CSV file first.' });
  };

  /** Modal cancel = only close */
  const closeModal = () => setIsModalOpen(false);

  /** Fully reset all component state */
  const handleReset = () => {
    resetFetchState();
    setSelectedFile(null);
    setUploadResult(null);
    setShowResult(false);
  };

  return (
    <div className={style.container}>
      {/* File picker area */}
      <IMEIFileHandler
        fetchState={fetchState}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        onReset={handleReset}
        onConfirmClick={openModal}
      />

      {/* Shows uploaded filename (before submit) + summary (after submit) */}
      <IMEIListDisplay
        selectedFile={selectedFile}
        showResult={showResult}
        uploadResult={uploadResult}
      />

      {/* Upload confirmation modal */}
      <IMEIConfirmationModal
        isOpen={isModalOpen}
        onConfirm={triggerUpload}
        onCancel={closeModal}
        isLoading={fetchState === FETCH_STATE.LOADING}
      />

      {/* Error block if upload failed */}
      {error && (
        <div className={style.error_text}>
          There was an error: {error.message || error.toString()}
        </div>
      )}
    </div>
  );
};

export default UploadDriveIMEIcsv;
